/**
 * AuthPage — login-only. Self-service signup is disabled by design:
 * accounts are provisioned by super-admins in Admin → Clients (pulled in
 * from WMS). The component therefore only renders the sign-in form.
 */
import { useState } from 'react';
import { useAuth } from './AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import './AuthPage.css';

function AuthPage({ onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sessionExpired } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="auth-back-link"
              data-testid="auth-back-link"
              style={{ background: 'none', border: 'none', color: '#96C11F', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              &larr; Back to home
            </button>
          )}
          <img
            src="https://customer-assets.emergentagent.com/job_scriptbuilder/artifacts/6hlprpgp_Green_Logo.png"
            alt="NicheCom"
            className="auth-logo-img"
          />
          <h1 className="auth-title">{sessionExpired ? 'Session expired' : 'Welcome Back'}</h1>
          <p className="auth-subtitle" data-testid="auth-subtitle">{sessionExpired ? 'For security you were signed out after a period of inactivity. Sign in to carry on where you left off.' : 'Sign in to NicheCom Social Tools'}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <Label htmlFor="email">Email Address</Label>
            <div className="input-wrapper">
              <Mail className="input-icon" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
                data-testid="email-input"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="password">Password</Label>
            <div className="input-wrapper">
              <Lock className="input-icon" />
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="auth-input"
                data-testid="password-input"
                autoComplete="current-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
            data-testid="auth-submit"
          >
            {loading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                Sign In
                <ArrowRight className="btn-icon" />
              </>
            )}
          </Button>
        </form>

        <div className="auth-switch" data-testid="auth-no-signup-note">
          <span className="switch-text" style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.78)', textAlign: 'center', lineHeight: 1.5 }}>
            Estate agents: sign in with your <strong style={{ color: '#fff' }}>WMS email and password</strong>.
            <br />
            Not working? Ask IT to enable your WMS access, or contact your NicheCom account manager.
          </span>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
